import { CSSObject, css } from '@emotion/react'
import {
  Color,
  FocusIndicator,
  FontWeight,
  Shadow,
  ShadowElevation,
  Size,
  Spacing,
  Theme,
  Typography,
  ZIndex,
} from '@opallabs/gem'

interface StyleProps {
  theme: Theme
  destructive?: boolean
  maxHeight?: number
}

export const list = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N000,
  color: Color[theme].Text.Default,
  ...Shadow[ShadowElevation.E1],
  ...Typography.T500,
  borderRadius: Size.Rounding,
  boxSizing: 'border-box',
  listStyleType: 'none',
  maxInlineSize: Spacing.XL * 6.25,
  minInlineSize: Spacing.M * 17.5,
  overflow: 'auto',
  // `menuItem` has a `margin-block-end` of `Spacing.XS`.
  paddingBlockStart: Spacing.XS,
  paddingInline: Spacing.XS,
  zIndex: ZIndex.Popover.Body,
  // Limit height to max available height as calculated by Radix
  maxHeight: 'var(--radix-dropdown-menu-content-available-height)',
  overflowY: 'auto',
})

export const item = ({ theme, destructive }: StyleProps): CSSObject => ({
  color: destructive ? Color[theme].Text.Warning : Color[theme].Text.Default,
  alignItems: 'center',
  border: 'none',
  borderRadius: Size.Rounding / 2,
  boxSizing: 'border-box',
  cursor: 'pointer',
  display: 'flex',
  marginBlockEnd: Spacing.XS,
  minBlockSize: Spacing.M * 2,
  paddingInline: Spacing.S,
  ...Typography.T500,

  '&[aria-disabled="true"]': {
    cursor: 'not-allowed',
    opacity: 0.5,

    '&:hover': {
      background: 'none',
      boxShadow: 'none !important',
    },
  },

  '&:hover': {
    backgroundColor: Color[theme]['Color/Primary/Primary-3'],
    boxShadow: 'none !important',
  },

  '&:focus': {
    ...FocusIndicator[theme],
  },

  '@media (prefers-contrast: more)': {
    fontWeight: FontWeight.Semibold,

    '&:hover': {
      backgroundColor: Color[theme].Surface.A200,
      color: Color[theme].Text.Default,
    },
  },
})

export const subMenuTrigger = css({
  justifyContent: 'space-between',
})

export const separator = ({ theme }: StyleProps): CSSObject => ({
  backgroundColor: Color[theme].Surface.N200,
  blockSize: Spacing.XS / 2,
  borderRadius: Size.Rounding / Spacing.XS,
  marginBlockEnd: Spacing.XS,

  '@media (prefers-contrast: more)': {
    backgroundColor: Color[theme].Text.Default,
  },
})

export const label = ({ theme }: StyleProps): CSSObject => ({
  ...Typography.T500,
  ...list,
  color: Color[theme].Text.Default,
  marginBlockEnd: Spacing.XS,
  marginBlockStart: Spacing.XS,
  opacity: 0.5,
  paddingInline: Spacing.S,
})

export const checkboxOverrides: CSSObject = {
  marginRight: 8,
}

export const arrow = (theme: Theme) =>
  css({
    fill: Color[theme].Surface.N000,
  })
